import React, { useContext, useEffect, useState } from 'react';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import TwDialog from '../../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import {
  DispatcherPayRateType,
  DispatcherResponse
} from '../../../redux/api/dispatchers/dispatcher.model';
import TwSelect, { TwSelectOption } from '../../../components/TwSelects/TwSelects';
import {
  Characters,
  useAllowCharacters,
  useUnformatValue
} from '../../../hooks/useAllowCharacters';
import StringUtils from '../../../utils/stringUtils';
import {
  useGetDispatcherByIdQuery,
  useUpdateDispatcherMutation
} from '../../../redux/api/dispatchers/dispatcherApi';
import { NotificationsContext } from '../../../providers/Notifications/Notifications.provider';
import { UpdateDispatcherData } from '../../../redux/api/dispatchers/dispatcher.model';
import './EditDispatcher.scss';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import useAuth from '../../../providers/Auth/Auth.provider';
export interface EditDispatcherProps {
  open: boolean;
  dispatcherId: string;
  onClose: () => void;
  onSuccess?: (updateDispatcher: DispatcherResponse) => void;
}

const PAY_RATE_TYPES: TwSelectOption[] = [
  {
    label: 'Per hour',
    value: 'PER_HOUR'
  },
  {
    label: 'Per mile',
    value: 'PER_MILE'
  },
  {
    label: 'Percentage',
    value: 'PERCENTAGE'
  }
];

const EditDispatcher: React.FC<EditDispatcherProps> = ({
  open,
  dispatcherId,
  onClose,
  onSuccess
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const [dispatcher, setDispatcher] = useState<DispatcherResponse | undefined>();
  const [dispatcherEmail, setDispatcherEmail] = useState<string>('');
  const [dispatcherPhone, setDispatcherPhone] = useState<string>('');
  const [payRate, setPayRate] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [selectedPayRateType, setSelectedPayRateType] = useState<
    DispatcherPayRateType | undefined
  >();

  const unformatString = useUnformatValue(Characters.number);
  const handleKeyDownCharacters = useAllowCharacters(Characters.phoneNumber);
  const { addNotification } = useContext(NotificationsContext);
  // RTK Query
  const { data: dispatcherData, error } = useGetDispatcherByIdQuery(dispatcherId ?? skipToken);
  const [updateDispatcher] = useUpdateDispatcherMutation();

  useEffect(() => {
    if (dispatcherData) {
      setDispatcher({
        ...dispatcherData,
        phone: StringUtils.formatPhoneNumber(dispatcherData.phone ?? '')
      });
    }

    if (error) {
      logRocketTrack('Team Management', {
        action: 'Edit dispatcher',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Could not get dispatcher information',
        variant: 'error'
      });
    }
  }, [dispatcherData, error]);

  useEffect(() => {
    if (!dispatcher) return;
    setDispatcherEmail(dispatcher.email_address || '');
    setDispatcherPhone(dispatcher.phone?.toString() || '');
    setPayRate(dispatcher.pay_rate || '');
    setFirstName(dispatcher.first_name || '');
    setLastName(dispatcher.last_name || '');
    setSelectedPayRateType(dispatcher.pay_rate_type || undefined);
  }, [dispatcher]);

  const handleSubmit = (): void => {
    const params: UpdateDispatcherData = {
      email_address: dispatcherEmail,
      phone: unformatString(dispatcherPhone),
      pay_rate: payRate,
      pay_rate_type: selectedPayRateType,
      first_name: firstName,
      last_name: lastName,
      id: dispatcherId
    };

    updateDispatcher(params)
      .unwrap()
      .then(() => {
        onClose();
      })
      .catch((error) => {
        console.error(error);
        logRocketTrack('Team Management', {
          action: error.data.message,
          severity: 'medium',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: `Error: ${error.data.message}`,
          variant: 'error'
        });
      });
  };

  const handleClose = (): void => {
    setSelectedPayRateType(undefined);
    onClose();
  };

  return (
    <TwDialog
      open={open}
      onClickClose={handleClose}
      size="sm"
      title="Edit dispatcher"
      actions={[
        <TwButton
          key="1"
          fullwidth={false}
          type={TwButtonType.secondary}
          label="Cancel"
          action={handleClose}
        />,
        <TwButton
          key="2"
          fullwidth={false}
          type={TwButtonType.primary}
          label="Save"
          action={handleSubmit}
        />
      ]}
      draggable={true}
    >
      <div className="edit-dispatcher__wrapper">
        <div className="edit-dispatcher__form-section">
          <div className="edit-dispatcher__inputs-container">
            <div className="edit-dispatcher__two-column">
              <div className="edit-dispatcher__two-column-item">
                <TwField
                  id="firstName"
                  name="firstName"
                  value={firstName}
                  label="First name"
                  inputType={TwInputTypes.text}
                  onInputChange={(e): void => {
                    setFirstName(e.target.value);
                  }}
                />
              </div>

              <div className="edit-dispatcher__two-column-item">
                <TwField
                  id="lastName"
                  name="lastName"
                  value={lastName}
                  label="Last name"
                  inputType={TwInputTypes.text}
                  onInputChange={(e): void => {
                    setLastName(e.target.value);
                  }}
                />
              </div>
            </div>

            <div className="edit-dispatcher__two-column">
              <div className="edit-dispatcher__two-column-item">
                <TwField
                  id="dispatcherEmail"
                  name="dispatcherEmail"
                  value={dispatcherEmail}
                  label="Email (optional)"
                  inputType={TwInputTypes.text}
                  onInputChange={(e): void => {
                    setDispatcherEmail(e.target.value);
                  }}
                />
              </div>

              <div className="edit-dispatcher__two-column-item">
                <TwField
                  id="dispatcherPhone"
                  name="dispatcherPhone"
                  value={dispatcherPhone}
                  label="Phone number (optional)"
                  inputType={TwInputTypes.text}
                  onInputChange={(e): void => {
                    setDispatcherPhone(e.target.value);
                  }}
                  onKeyDown={handleKeyDownCharacters}
                  placeholder="(_ _ _)  _ _ _ - _ _ _ _"
                />
              </div>
            </div>

            <div className="edit-dispatcher__two-column">
              <div className="edit-dispatcher__two-column-item">
                <TwSelect
                  id="payRateType"
                  name="payRateType"
                  optionList={PAY_RATE_TYPES}
                  value={selectedPayRateType}
                  label="Default pay rate type (optional)"
                  onInputChange={(e): void => {
                    const type =
                      e.target.value === 'PERCENTAGE'
                        ? 'PERCENTAGE'
                        : e.target.value === 'PER_HOUR'
                        ? 'PER_HOUR'
                        : e.target.value === 'PER_MILE'
                        ? 'PER_MILE'
                        : 'PERCENTAGE';
                    setSelectedPayRateType(type);
                  }}
                />
              </div>
              <div className="edit-dispatcher__two-column-item">
                <TwField
                  id="payRate"
                  name="payRate"
                  value={payRate}
                  label="Rate (optional)"
                  inputType={TwInputTypes.number}
                  onInputChange={(e): void => {
                    setPayRate(e.target.value);
                  }}
                  startAdorment={
                    selectedPayRateType !== 'PERCENTAGE' ? (
                      <span className="edit-dispatcher__rate-adornment">$</span>
                    ) : null
                  }
                  endAdorment={
                    selectedPayRateType === 'PERCENTAGE' ? (
                      <span className="edit-dispatcher__rate-adornment">%</span>
                    ) : null
                  }
                  className="edit-dispatcher__pay-rate"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </TwDialog>
  );
};

export default EditDispatcher;
